import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { api, type AirPlatformCriterion } from '../../api';

/**
 * The AI Platform Selection Criteria editor — the weighted list of platform aspects.
 *
 * ONE COMPONENT, TWO PLACES (owner-directed, 2026-08-17): Settings → AI Platform Selection →
 * AI Platform Selection Criteria, and the "Platform Capabilities" tab of Platform Fitment
 * Criteria. The owner asked for the second to show the first's content, and two copies of
 * an editable list is how the two drift apart and start disagreeing about what the criteria
 * are.
 *
 * `disabled` is what the fitment screen's lock passes in. That lock declares itself "the
 * fixed standard for all analyses (capability, readiness & weighting)", and this tab IS the
 * capability half — so a lock that left it editable would not be a lock.
 *
 * Everything below is the screen's existing behaviour unchanged: explicit save, a dimmed
 * unselected row, the 1-5 slider, and the platform's canonical 🗑️ delete.
 */

/** Application Master Data's action-button boxes, reproduced so the glyphs sit identically. */
const ACTION_BTN: React.CSSProperties = {
  padding: '4px 6px',
  borderRadius: '4px',
  border: '1px solid #1976d2',
  color: '#1976d2',
  background: 'transparent',
  fontSize: 14,
  lineHeight: 1,
  cursor: 'pointer',
};

const DANGER_BTN: React.CSSProperties = { ...ACTION_BTN, borderColor: '#d32f2f', color: '#d32f2f' };
export function PlatformCriteriaEditor({ disabled = false }: { disabled?: boolean }) {
  const [criteria, setCriteria] = useState<AirPlatformCriterion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const load = () => {
    setLoading(true);
    api.aiReadiness
      .getPlatformCriteria()
      .then((d) => setCriteria(d.criteria))
      .catch((e) => setError(e?.message || 'Could not load the criteria.'))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const setRow = (id: number, patch: Partial<AirPlatformCriterion>) =>
    setCriteria((prev) => prev.map((c) => (c.id === id ? { ...c, ...patch } : c)));

  const save = async () => {
    setSaving(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await api.aiReadiness.savePlatformCriteria(
        criteria.map((c) => ({ id: c.id, selected: c.is_selected, weight: c.weight })),
      );
      setNotice('Criteria saved.');
    } catch (e) {
      setError((e as Error).message || 'Could not save.');
    } finally {
      setSaving(false);
    }
  };

  const add = async () => {
    const name = newName.trim();
    if (!name) {
      setError('Enter an aspect name');
      return;
    }
    setError(undefined);
    try {
      await api.aiReadiness.addPlatformCriterion(name, newDescription.trim());
      setNewName('');
      setNewDescription('');
      load();
    } catch (e) {
      setError((e as Error).message || 'Could not add the aspect.');
    }
  };

  const remove = async (id: number) => {
    try {
      await api.aiReadiness.deletePlatformCriterion(id);
      setCriteria((prev) => prev.filter((c) => c.id !== id));
    } catch (e) {
      setError((e as Error).message || 'Could not remove the aspect.');
    }
  };

  return (
    <>
      {/* The source's lead paragraph, verbatim. */}
      <Typography sx={{ fontSize: '0.86rem', color: '#616161', maxWidth: 900, m: '0 0 14px' }}>
        Choose which aspects matter when selecting an AI platform and how much each one
        weighs. The <strong>AI Platform Fit</strong> analysis uses these selected, weighted
        criteria — together with the opportunity&apos;s scoring Q&amp;A and documents — so fit
        scores reflect real selection trade-offs (cost, ease of learning, deployment speed,
        lock-in, …), not just raw capability.
      </Typography>

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
        <Button variant="contained" onClick={() => void save()} disabled={saving || loading || disabled}>
          {saving ? 'Saving…' : 'Save criteria'}
        </Button>
        {notice ? (
          <Typography sx={{ fontSize: 13, color: 'success.main' }}>{notice}</Typography>
        ) : null}
        {error ? <Typography sx={{ fontSize: 13, color: 'error.main' }}>{error}</Typography> : null}
      </Box>

      {loading ? (
        <Typography sx={{ fontSize: 13, color: '#999' }}>Loading…</Typography>
      ) : (
        <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflow: 'hidden' }}>
          {criteria.map((c, i) => (
            <Box
              key={c.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 1.5,
                py: 1,
                borderTop: i === 0 ? 'none' : '1px solid #eef1f6',
                // The source dims an unselected row rather than hiding it.
                opacity: c.is_selected ? 1 : 0.5,
              }}
            >
              <Checkbox
                size="small"
                disabled={disabled}
                checked={c.is_selected}
                onChange={(e) => setRow(c.id, { is_selected: e.target.checked })}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 14, color: '#212121' }}>{c.name}</Typography>
                {c.description ? (
                  <Typography sx={{ fontSize: 12, color: '#8a95a8' }}>{c.description}</Typography>
                ) : null}
              </Box>
              <Slider
                size="small"
                disabled={disabled}
                min={1}
                max={5}
                step={1}
                marks
                value={c.weight}
                onChange={(_, v) => setRow(c.id, { weight: v as number })}
                sx={{ width: 170, flexShrink: 0 }}
              />
              <Typography
                sx={{ width: 18, textAlign: 'center', fontSize: 13, fontVariantNumeric: 'tabular-nums' }}
              >
                {c.weight}
              </Typography>
              <button
                type="button"
                title="Remove aspect"
                aria-label="Remove aspect"
                style={disabled ? { ...DANGER_BTN, opacity: 0.4, cursor: 'not-allowed' } : DANGER_BTN}
                disabled={disabled}
                onClick={() => void remove(c.id)}
              >
                🗑️
              </button>
            </Box>
          ))}
        </Box>
      )}

      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Aspect name (e.g. Sustainability)"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          sx={{ minWidth: 220 }}
        />
        <TextField
          size="small"
          placeholder="Short description (optional)"
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          sx={{ flex: 1, minWidth: 260 }}
        />
        <Button onClick={() => void add()} disabled={disabled}>
          Add
        </Button>
      </Box>
    </>
  );
}
